import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Use environment variable for production builds, fall back to /api for local development.
// When VITE_USE_PROD_API=true in dev mode, always use proxy (/api) to reach production.
const useProdApi = import.meta.env.DEV && import.meta.env.VITE_USE_PROD_API === 'true';
const rawApiUrl = useProdApi ? undefined : (import.meta.env.VITE_API_URL as string | undefined);
const normalizedApiUrl = rawApiUrl ? rawApiUrl.replace(/\/$/, '') : undefined;
const API_BASE =
    normalizedApiUrl
        ? (normalizedApiUrl.endsWith('/api') ? normalizedApiUrl : `${normalizedApiUrl}/api`)
        : '/api';

console.log('[AuthContext] VITE_API_URL:', rawApiUrl, '-> API_BASE:', API_BASE);

interface User {
    id: string;
    email: string;
    name: string;
    household_id: string;
}

interface AuthResponse {
    success: boolean;
    data?: {
        token: string;
        user: User;
    };
    message?: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, name: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check for existing token
        const savedToken = localStorage.getItem('token');
        if (savedToken) {
            verifyToken(savedToken);
        } else {
            setLoading(false);
        }
    }, []);

    async function verifyToken(savedToken: string) {
        try {
            const res = await fetch(`${API_BASE}/auth/me`, {
                headers: { Authorization: `Bearer ${savedToken}` },
            });
            if (res.ok) {
                const data: { data?: User } = await res.json();
                if (data.data) {
                    setUser(data.data);
                    setToken(savedToken);
                }
            } else {
                localStorage.removeItem('token');
            }
        } catch (error) {
            console.error('Token verification failed:', error);
            localStorage.removeItem('token');
        } finally {
            setLoading(false);
        }
    }

    async function login(email: string, password: string) {
        const res = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });

        const data: AuthResponse = await res.json();
        if (!data.success || !data.data) {
            throw new Error(data.message || 'Login failed');
        }

        localStorage.setItem('token', data.data.token);
        setToken(data.data.token);
        setUser(data.data.user);
    }

    async function register(email: string, password: string, name: string) {
        const res = await fetch(`${API_BASE}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name }),
        });

        const data: AuthResponse = await res.json();
        if (!data.success || !data.data) {
            throw new Error(data.message || 'Registration failed');
        }

        localStorage.setItem('token', data.data.token);
        setToken(data.data.token);
        setUser(data.data.user);
    }

    function logout() {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                isAuthenticated: !!token,
                loading,
                login,
                register,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
